"use client"

import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query"
import { toast } from "sonner"

type OptimisticMutationConfig<TData, TVariables> = {
    queryKey: QueryKey
    mutationFn: (variables: TVariables) => Promise<unknown>
    optimisticUpdate: (oldData: TData[] | undefined, variables: TVariables) => TData[]
    showErrorToast?: boolean
    showSuccessToast?: boolean
    successMessage?: string
    errorMessage?: string
    onSuccess?: (data: TData, variables: TVariables) => void
}

export function useOptimisticMutation<TData, TVariables>({
    queryKey,
    mutationFn,
    optimisticUpdate,
    showErrorToast = true,
    showSuccessToast = false,
    successMessage = "Success",
    errorMessage = "Something went wrong",
    onSuccess,
}: OptimisticMutationConfig<TData, TVariables>) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn,
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey })

            const previousData = queryClient.getQueriesData<TData[]>({ queryKey })

            queryClient.setQueriesData<TData[]>({ queryKey }, (old) =>
                optimisticUpdate(old, variables)
            )

            return { previousData }
        },
        onError: (error, _variables, context) => {
            if (context?.previousData) {
                for (const [key, data] of context.previousData) {
                    queryClient.setQueryData(key, data)
                }
            }
            if (showErrorToast) {
                const message = error instanceof Error ? error.message : errorMessage
                toast.error(message)
            }
        },
        onSuccess: (data, variables) => {
            if (showSuccessToast) {
                toast.success(successMessage)
            }
            onSuccess?.(data as TData, variables)
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey })
        },
    })
}
